import { useLayoutEffect } from "react";

const MARGIN = 8;
export const SCREEN_EDGE_PADDING = 15;
const BASE_STYLE = { position: "fixed", zIndex: 1080 };
export const TRANSFORM_ABOVE = "translateX(-50%) translateY(calc(-100% - 8px))";
export const TRANSFORM_BELOW = "translateX(-50%) translateY(8px)";

function getNavbarBottom() {
  return document.querySelector(".navbar")?.getBoundingClientRect().bottom ?? 0;
}

function clampHorizontalCenter(idealLeft, elementWidth, viewportWidth) {
  const halfWidth = elementWidth / 2;
  return Math.max(
    SCREEN_EDGE_PADDING + halfWidth,
    Math.min(idealLeft, viewportWidth - SCREEN_EDGE_PADDING - halfWidth),
  );
}

function applyPositioning(element, rect) {
  const navbarBottom = getNavbarBottom();
  // Prefer sitting above the rect; drop below when there isn't room between it and the navbar.
  const showAbove = rect.top - navbarBottom > element.offsetHeight + MARGIN;
  const left = clampHorizontalCenter(
    rect.left + rect.width / 2,
    element.offsetWidth,
    window.innerWidth,
  );
  element.style.left = `${left}px`;
  element.style.top = `${showAbove ? rect.top : rect.top + rect.height}px`;
  element.style.transform = showAbove ? TRANSFORM_ABOVE : TRANSFORM_BELOW;
}

/**
 * Keeps a fixed-position element horizontally centered on a viewport rect,
 * flipping above or below the rect based on available space. Renders hidden on
 * the first paint, then measures the real element and applies positioning before
 * the browser paints (via useLayoutEffect).
 *
 * @param {React.RefObject} elementRef - ref to the outer div being positioned
 * @param {Function|null} getViewportRect - live rect callback; RAF loop runs when provided
 * @returns {{ style: object }}
 */
export default function useAnchoredToRect(elementRef, getViewportRect) {
  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element || !getViewportRect) return;

    let frameId;
    const update = () => {
      if (element) {
        applyPositioning(element, getViewportRect());
        element.style.visibility = "visible";
      }
      frameId = requestAnimationFrame(update);
    };
    frameId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(frameId);
  }, [getViewportRect, elementRef]);

  //Nothing to anchor the element to - pin it to the center of the screen
  if (!getViewportRect) {
    return {
      style: {
        ...BASE_STYLE,
        top: "50%",
        left: "50%",
        transform: "translateX(-50%) translateY(-50%)",
      },
    };
  }

  //Skip the first render loop so you can measure how much space the element needs
  return { style: { ...BASE_STYLE, visibility: "hidden" } };
}
